"use client";

import { autoPlacement } from "@floating-ui/dom";
import {
    autoUpdate,
    FloatingFocusManager,
    useDismiss,
    useFloating,
    useInteractions,
    useMergeRefs,
    useRole,
} from "@floating-ui/react";
import cn from "clsx";
import {
    forwardRef,
    InputHTMLAttributes,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useState,
} from "react";
import { DayPickerProps } from "react-day-picker";
import { FiCalendar } from "react-icons/fi";
import { getFieldDescriptionId } from "../../form-fields/field-description";
import { FieldProps } from "../../form-fields/field-props";
import { Textbox } from "../../text-fields/textbox";
import { Calendar } from "../calendar";
import {
    CalendarValue,
    convertToCalendarValue,
    formatLabel,
    SharedCalendarProps,
} from "../shared/calendar-utils";
import { CalendarFooter } from "../shared/CalendarFooter";
import { CalendarWrapper } from "../shared/CalendarWrapper";
import styles from "./CalendarSelect.module.scss";
import { useInput } from "./useInput";

export type CalendarSelectProps = SharedCalendarProps &
    FieldProps &
    Omit<
        InputHTMLAttributes<HTMLInputElement>,
        "onChange" | "value" | "defaultValue"
    > & {
        /**
         * Handles passing the selected date to controlled forms
         * @param date
         * @returns
         */
        onChange?: (date?: CalendarValue | null) => void;
        /**
         * Callback for a done button that displays in the footer when this function is provided
         */
        onDone?: React.MouseEventHandler<HTMLOrSVGElement>;
    };

export const CalendarSelect = forwardRef<HTMLInputElement, CalendarSelectProps>(
    (
        {
            isRangeSelect = false,
            fieldId,
            errorText,
            label,
            required,
            disabled,
            descriptionText,
            placeholder = "",
            footer,
            onSelected,
            dayPickerOptions,
            value,
            onChange,
            onBlur,
            onDone,
            defaultValue,
            onNavigate,
            onReset,
            wrapperClassName,
            ...inputProps
        },
        ref,
    ) => {
        const fallbackId = useId();
        const calendarId = fieldId || fallbackId;
        const [isOpen, setIsOpen] = useState(false);
        const [initFocus, setInitFocus] = useState(true);
        const isControlled = value !== undefined;
        const [defaultDate] = useState(
            convertToCalendarValue(defaultValue || value),
        );
        const dateValue = useMemo(
            () =>
                isControlled
                    ? convertToCalendarValue(value) || null
                    : undefined,
            [value, isControlled],
        );

        const handleSelect = useCallback(
            (selected?: CalendarValue) => {
                if (!disabled) {
                    const selectedValue =
                        isControlled && !selected ? null : selected;
                    onSelected?.(selectedValue);
                    onChange?.(selectedValue);
                }
            },
            [disabled, isControlled, onSelected, onChange],
        );
        // @ts-ignore https://github.com/gpbl/react-day-picker/issues/1583
        const {
            inputProps: dayPickerInputProps,
            dayPickerProps: pickerOptions,
            setSelected,
            reset,
        } = useInput({
            format: "MM/dd/yyyy",
            ...dayPickerOptions,
            isRangeSelect,
            placeholder,
            required,
            defaultSelected:
                defaultDate || convertToCalendarValue(value) || undefined,
            onCalendarChange: (obj) => {
                if (obj.updated) {
                    handleSelect(obj.date);
                }
            },
        });
        const { selected } = pickerOptions;

        useEffect(() => {
            if (dateValue !== undefined) {
                setSelected(dateValue || undefined);
            }
        }, [dateValue, setSelected]);

        const { refs, floatingStyles, context } = useFloating({
            whileElementsMounted: autoUpdate,
            open: isOpen,
            onOpenChange: setIsOpen,
            middleware: [
                autoPlacement({
                    autoAlignment: false,
                    allowedPlacements: ["bottom-start", "top-start"],
                }),
            ],
        });

        const { getReferenceProps, getFloatingProps } = useInteractions([
            useRole(context, { role: "dialog" }),
            useDismiss(context, {
                enabled: true,
                escapeKey: true,
            }),
        ]);

        const mergedRefs = useMergeRefs([ref, refs.setReference]);

        return (
            <CalendarWrapper
                className={cn(styles.wrapper, wrapperClassName, {
                    [styles["wrapper--open"]]: isOpen,
                })}
                fieldId={calendarId}
                errorText={errorText}
                disabled={disabled}
                descriptionText={descriptionText}
                required={required}
                label={label}
            >
                <Textbox
                    className={styles.input}
                    wrapperClassName={cn(styles["input-wrapper"], {
                        [styles["input-wrapper--error"]]: errorText,
                    })}
                    hasError={!!errorText}
                    {...getReferenceProps({
                        ...dayPickerInputProps,
                        ref: mergedRefs,
                        id: calendarId,
                        role: "combobox",
                        "aria-autocomplete": "none",
                        "aria-required": required || undefined,
                        "aria-invalid": !!errorText || undefined,
                        "aria-disabled": disabled || undefined,
                        "aria-describedby":
                            getFieldDescriptionId(calendarId) || undefined,
                        placeholder: undefined,
                        autoComplete: "off",
                        disabled,
                        ...inputProps,
                        onKeyDown: (e) => {
                            if (!disabled) {
                                switch (e.key) {
                                    case "ArrowDown":
                                    case " ":
                                        e.preventDefault();
                                        setIsOpen(true);
                                        setInitFocus(true);
                                        return false;
                                    default:
                                }
                            }
                        },
                    })}
                    onClick={(e) => {
                        if (!disabled) {
                            setIsOpen(true);
                            setInitFocus(false);
                            inputProps.onClick?.(e);
                        }
                    }}
                    adornRight={
                        <FiCalendar
                            className={styles.icon}
                            aria-hidden
                            role="img"
                        />
                    }
                >
                    <span
                        className={cn(styles["display-label"], {
                            [styles["display-label--unselected"]]: !selected,
                            [styles["display-label--disabled"]]: disabled,
                        })}
                    >
                        {formatLabel(placeholder, selected)}
                    </span>
                </Textbox>
                {isOpen && (
                    <FloatingFocusManager
                        context={context}
                        modal={true}
                        initialFocus={initFocus ? 0 : -1}
                    >
                        <div
                            className={cn(styles.calendar, {
                                [styles["calendar--selected"]]: selected,
                                [styles["calendar--error"]]: errorText,
                                [styles["calendar--disabled"]]: disabled,
                            })}
                            {...getFloatingProps({
                                ref: refs.setFloating,
                                style: floatingStyles,
                            })}
                        >
                            <Calendar
                                className={styles["inner-calendar"]}
                                dayPickerOptions={
                                    {
                                        initialFocus: initFocus,
                                        ...dayPickerOptions,
                                        ...pickerOptions,
                                    } as DayPickerProps
                                }
                                onSelect={handleSelect}
                                onNavigate={onNavigate}
                                disableAll={disabled}
                            />
                            <CalendarFooter
                                className={styles.footer}
                                disableReset={disabled || !selected}
                                disableDone={disabled}
                                onReset={() => {
                                    reset();
                                    handleSelect(
                                        required ? defaultDate : undefined,
                                    );
                                    onReset?.();
                                }}
                                onDone={(e) => {
                                    onDone?.(e);
                                    setIsOpen(false);
                                }}
                            >
                                {footer}
                            </CalendarFooter>
                        </div>
                    </FloatingFocusManager>
                )}
            </CalendarWrapper>
        );
    },
);

CalendarSelect.displayName = "CalendarSelect";
